import { Injectable, inject } from '@angular/core';
import { NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { I18NextPipe } from 'angular-i18next';
import { MbsSize, ModalSettings, ToastService } from 'mbs-ui-kit';
import { ComputerAuthorizationModalComponent } from 'mbs-ui-shared@components/computers-modal/computer-authorization/computer-authorization-modal.component';
import { ComputersAuthRequiredModalComponent } from 'mbs-ui-shared@components/computers-modal/computers-auth-required-modal/computers-auth-required-modal.component';
import { ComputersBulkInstallAgentConfirmationModalComponent } from 'mbs-ui-shared@components/computers-modal/computers-bulk-install-agent-confirmation-modal/computers-bulk-install-agent-confirmation-modal.component';
import { OfflineEditVersionModalComponent } from 'mbs-ui-shared@components/computers-modal/offline-edit-version-modal/offline-edit-version-modal.component';
import { SelectComputersModalComponent } from 'mbs-ui-shared@components/select-computers-modal/select-computers-modal.component';
import { PermissionsEnum } from 'mbs-ui-shared@models';
import Computer, { AgentType } from 'mbs-ui-shared@models/Computer';
import { ComputerBasedModal, ComputerPlanBasedModal } from 'mbs-ui-shared@models/ComputersModals';
import { PlanInfo } from 'mbs-ui-shared@models/backup/plan-info-model';
import { EMPTY, Observable, catchError, from, map, noop, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ModalComputersData } from 'wizards/onboarding/steps/try-rmm/installation-mode/installationMode.constants';
import { ModalWrapper } from '../wrappers/modal.wrapper';

@Injectable({
  providedIn: 'root'
})
export class ComputerModalsFacade {
  private modalService = inject(ModalWrapper);
  private i18nPipe = inject(I18NextPipe);
  private toastService = inject(ToastService);

  public openComputerBasedModal(modalComponent, computer: Computer, settings?: ModalSettings) {
    const modal = this.modalService.openRef(modalComponent, settings);

    if (!modal) return modal;

    (modal.componentInstance as ComputerBasedModal).computer = computer;
    modal.result.catch(noop);
    return modal;
  }

  public openPlanBasedModal(modalComponent, computer?: Computer, plan?: PlanInfo): NgbModalRef {
    const modal = this.modalService.openRef(modalComponent);

    (modal.componentInstance as ComputerPlanBasedModal).computer = computer;
    (modal.componentInstance as ComputerPlanBasedModal).plan = plan;
    modal.result.catch(noop);

    return modal;
  }

  public showInstallationCompletedToast(agent: AgentType): void {
    this.toastService.success(this.getInstallationCompletedMessage(agent));
  }

  public openInstallationStartedModal(agent, computer?: Computer): Observable<boolean> {
    return this.openInfoModal(this.getInstallationStartedMessage(agent, computer));
  }

  public getInstallationTitle(agent: AgentType): string {
    return this.getAgentInstallationMessages(agent).join('');
  }

  private getAgentInstallationMessages(agent: AgentType): string[] {
    return [this.i18nPipe.transform(`shared:products.${agent}`), this.i18nPipe.transform('shared:computers.modals.installationOf')];
  }

  private getInstallationCompletedMessage(agent: AgentType): string {
    return [...this.getAgentInstallationMessages(agent), this.i18nPipe.transform('shared:computers.modals.completed')].join('');
  }

  private getInstallationStartedMessage(agent: AgentType, computer?: Computer): string {
    return [
      ...this.getAgentInstallationMessages(agent),
      computer
        ? this.i18nPipe.transform('shared:computers.modals.startedOn', { param: Computer.getComputerName(computer) })
        : this.i18nPipe.transform('shared:computers.modals.started')
    ].join('');
  }

  public openInfoModal(body: string, footerSettings?, title?: string) {
    const header = { title: title || this.i18nPipe.transform('shared:common.info', { format: 'title' }) };
    const footer = footerSettings || this.getInfoModalFooter(this.i18nPipe.transform('shared:buttons.close', { format: 'title' }), null);

    return from(this.modalService.open({ header, footer }, body)).pipe(catchError(() => of(false)));
  }

  public getInfoModalFooter(okButtonCaption: string, cancelButtonCaption: string = null) {
    return { okButton: { text: okButtonCaption }, cancelButton: { show: !!cancelButtonCaption, text: cancelButtonCaption } };
  }

  public openNoPublicBuildsModal(showCloseButtonOnly = false) {
    return this.openInfoModal(
      this.i18nPipe.transform('shared:computers.modals.noPublicBuilds'),
      this.getInfoModalFooter(
        this.i18nPipe.transform('shared:buttons.close', { format: 'title' }),
        showCloseButtonOnly ? null : this.i18nPipe.transform('shared:computers.downloads', { format: 'title' })
      )
    ).pipe(
      map((res) => !!res),
      catchError(() => of(false))
    );
  }

  public openSQLPlanModal() {
    const body = `${this.i18nPipe.transform('shared:computers.modals.sqlPlanTextFirstRow')}<br />${this.i18nPipe.transform(
      'shared:computers.modals.sqlPlanTextLastRow'
    )}`;
    return from(
      this.modalService.open(
        {
          header: { title: this.i18nPipe.transform('shared:computers.modals.sqlPlanCaption', { format: 'title' }) },
          footer: this.getInfoModalFooter(this.i18nPipe.transform('shared:buttons.open'))
        },
        body
      )
    );
  }

  public openEC2PlanModal() {
    const body = `${this.i18nPipe.transform('shared:computers.modals.EC2PlanTextFirstRow')}`;
    return from(
      this.modalService.open(
        {
          header: { title: this.i18nPipe.transform('shared:computers.modals.EC2PlanCaption', { format: 'title' }) },
          footer: this.getInfoModalFooter(this.i18nPipe.transform('shared:buttons.open'))
        },
        body
      )
    );
  }

  public openComputerAuthorizationModal(computers: Computer[]) {
    const modal = this.modalService.openRef(ComputerAuthorizationModalComponent, {
      permissions: [PermissionsEnum.Users, PermissionsEnum.ManageCompanies],
      missingPermissionActionName: this.i18nPipe.transform('shared:computers.modals.computerAuthorizationActionName')
    });

    if (!modal) return modal;

    (modal.componentInstance as ComputerAuthorizationModalComponent).computers = computers;
    modal.result.catch(noop);
    return modal;
  }

  public openInstallAgentModal(agent: AgentType.Backup | AgentType.RMM, computer: Computer) {
    const header = {
      title: this.i18nPipe.transform(`shared:computers.modals.${agent === AgentType.Backup ? 'installBackupCaption' : 'installRMMCaption'}`)
    };
    const bodyText = this.i18nPipe.transform(
      `shared:computers.modals.${agent === AgentType.Backup ? 'installBackupQuestion' : 'installRMMQuestion'}`,
      { param: Computer.getComputerName(computer) }
    );
    const body = `<div class="row"><div class="col-auto">${bodyText}</div></div>`;
    const footer = this.getInfoModalFooter(this.i18nPipe.transform('shared:buttons.install'));
    return from(this.modalService.open({ header, footer }, body));
  }

  public openBulkInstallAgentModal(agent: AgentType) {
    if (agent === AgentType.DeepInst) {
      const header = { title: this.i18nPipe.transform('shared:computers.modals.installBulkDeepInstCaption') };
      const body = `<div class="row"><div class="col-auto">
      ${this.i18nPipe.transform('shared:computers.modals.installBulkDeepInstBody')}
      </div></div>
      <div class="row"><div class="col-auto">
      <ul class="mt-3"><li>
      ${this.i18nPipe.transform('shared:computers.modals.installBulkDeepInstOption1')}
      </li><li>
      ${this.i18nPipe.transform('shared:computers.modals.installBulkDeepInstOption2')}
      </li><li>
      ${this.i18nPipe.transform('shared:computers.modals.installBulkDeepInstOption3')}
      </li><li>
      ${this.i18nPipe.transform('shared:computers.modals.installBulkDeepInstOption4')}
      </li></ul>
      </div></div>
      <div class="row"><div class="col-auto">
      <b>${this.i18nPipe.transform('shared:computers.modals.install')}</b>${this.i18nPipe.transform(
        'shared:computers.modals.installBulkDeepInstQuestion'
      )}
      </div></div>
      `;
      const footer = this.getInfoModalFooter(this.i18nPipe.transform('shared:buttons.install'));
      return from(this.modalService.open({ header, size: MbsSize.sm, footer }, body));
    } else {
      const modal = this.modalService.openRef(ComputersBulkInstallAgentConfirmationModalComponent);
      const modalInstance = modal.componentInstance as ComputersBulkInstallAgentConfirmationModalComponent;

      modalInstance.agent = agent;

      return from(modal.result);
    }
  }

  public openSimpleModal(modalComponent) {
    const modal = this.modalService.openRef(modalComponent);
    modal.result.catch(noop);
    return modal;
  }

  public openWizard(content, settings?: NgbModalOptions) {
    return this.modalService.openWizard(content, settings).catch(noop);
  }

  public authRequiredHandle(computer: Computer): Observable<unknown> {
    const modal = this.openComputerBasedModal(ComputersAuthRequiredModalComponent, computer);

    return from(modal.result).pipe(
      switchMap(() => this.authorizeComputer(computer)),
      catchError(() => of(false))
    );
  }

  public authorizeComputer(computer: Computer): Observable<unknown> {
    const modal = this.openComputerAuthorizationModal([computer]);

    if (!modal) {
      return of(false);
    }

    return from(modal.result.catch(noop));
  }

  public openOfflineEditLowVersionModal(): void {
    this.modalService.openRef(OfflineEditVersionModalComponent)?.result?.then(noop).catch(noop);
  }

  public showSelectComputerModal(modalSettings: ModalSettings): Observable<ModalComputersData> {
    return from(this.modalService.openCustom(SelectComputersModalComponent, modalSettings)).pipe(
      catchError(() => EMPTY),
      map((result: ModalComputersData) => result)
    );
  }
}
